import { createSlice } from '@reduxjs/toolkit';
import userService from '../services/userService';
import providerService from '../services/providerService';
import { PagedList } from './models/paged-list.model';
import { Provider, ProviderOptimized } from './models/provider.model';
import { SortModel } from './models/sort-model.model';
import { UserRole } from './models/user-role.model';
import { User } from './models/user.model';

interface UserState {
  userInfo: any | null;
  loadingUserInfo: boolean;
  loadedUserInfo: boolean;
  eulaInfo: any | null;
  externalSupportAgreementInfo: any | null;

  searchFilter: string;
  selectedFilter: string;
  page: number;
  pageSize: number;
  sortModel: SortModel | null;
  loadingUserList: boolean;
  loadedUserList: boolean;
  userList: PagedList<User>;
  onboardingUsers: User[];

  loadingUser: boolean;
  loadedUser: boolean;
  savingUser: boolean;
  savedUser: boolean;
  savingError: string | null;
  user: User | null;

  loadingUserRoles: boolean;
  loadedUserRoles: boolean;
  userRoles: UserRole[];

  providerSearchFilter: string;
  providerList: PagedList<ProviderOptimized>;
  loadingProviderList: boolean;
  loadedProviderList: boolean;

  onboardingUser: boolean;
  onboardedUser: boolean;
  onboardingUserError: string | null;
  onboardingUserSuccess: string | null;

  userSnackbarMessage: string | null;
  userSnackbarError: string | null;
}

export const initialState: UserState = {
  userInfo: null,
  loadingUserInfo: false,
  loadedUserInfo: false,
  eulaInfo: null,
  externalSupportAgreementInfo: null,

  searchFilter: '',
  selectedFilter: 'active0',
  page: 1,
  pageSize: 10,
  sortModel: null,
  loadingUserList: false,
  loadedUserList: false,
  userList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  onboardingUsers: [],

  loadingUser: false,
  loadedUser: false,
  savingUser: false,
  savedUser: false,
  savingError: null,
  user: null,

  userRoles: [],
  loadingUserRoles: false,
  loadedUserRoles: false,

  providerSearchFilter: '',
  providerList: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  loadingProviderList: false,
  loadedProviderList: false,

  onboardingUser: false,
  onboardedUser: false,
  onboardingUserError: null,
  onboardingUserSuccess: null,

  userSnackbarMessage: null,
  userSnackbarError: null,
};

export const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUserInfo: (state: UserState, action: { type: string; payload: any }) => {
      state.userInfo = action.payload;
    },
    setLoadingUserInfo: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUserInfo = action.payload;
    },
    setLoadedUserInfo: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUserInfo = action.payload;
    },
    setEULAInfo: (state: UserState, action: { type: string; payload: any }) => {
      state.eulaInfo = action.payload;
    },
    setUserEULA: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.userInfo = {
        ...state.userInfo,
        agreedToEULA: action.payload,
      };
    },
    setExternalSupportAgreementInfo: (
      state: UserState,
      action: { type: string; payload: any },
    ) => {
      state.externalSupportAgreementInfo = action.payload;
    },
    setUserExternalSupportAgreement: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.userInfo = {
        ...state.userInfo,
        agreedToExternalSupportAgreement: action.payload,
      };
    },
    setSearchFilter: (state: UserState, action: { payload: string }) => {
      state.searchFilter = action.payload;
    },
    setSelectedFilter: (state: UserState, action: { payload: string }) => {
      state.selectedFilter = action.payload;
    },
    setPage: (state: UserState, action: { payload: number }) => {
      state.page = action.payload;
    },
    setPageSize: (state: UserState, action: { payload: number }) => {
      state.pageSize = action.payload;
    },
    setSortModel: (state: UserState, action: { payload: SortModel | null }) => {
      state.sortModel = action.payload;
    },
    setLoadingUserList: (state: UserState, action: { payload: boolean }) => {
      state.loadingUserList = action.payload;
    },
    setLoadedUserList: (state: UserState, action: { payload: boolean }) => {
      state.loadedUserList = action.payload;
    },
    setUserList: (state: UserState, action: { payload: PagedList<User> }) => {
      state.loadingUserList = false;
      state.loadedUserList = true;
      state.userList = action.payload;
    },
    setOnboardingUsers: (state: UserState, action: { payload: User[] }) => {
      state.onboardingUsers = action.payload;
    },

    setLoadingUser: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUser = action.payload;
    },
    setLoadedUser: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUser = action.payload;
    },

    setSavingUser: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingUser = action.payload;
    },
    setSavedUser: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedUser = action.payload;
    },
    setSavingError: (
      state: UserState,
      action: { type: string; payload: string | null },
    ) => {
      state.savingError = action.payload;
    },
    resetSavingFlags: (state: UserState) => {
      state.savingUser = false;
      state.savedUser = false;
      state.savingError = null;
    },
    setUser: (
      state: UserState,
      action: { type: string; payload: User | null },
    ) => {
      state.user = action.payload;
    },
    addUser: (
      state: UserState,
      action: { type: string; payload: User | null },
    ) => {
      state.loadingUser = false;
      state.loadedUser = !!action.payload;
      state.user = action.payload;
    },
    updateUser: (
      state: UserState,
      action: { type: string; payload: User | null },
    ) => {
      state.loadingUser = false;
      state.loadedUser = !!action.payload;
      state.user = action.payload;
      if (action.payload) {
        const idx = state.userList.items.findIndex(
          (u) => u.userAccountID === action.payload?.userAccountID,
        );
        const newItems = [...state.userList.items];
        newItems.splice(idx, 1, action.payload);
        state.userList = {
          ...state.userList,
          items: newItems,
        };
      }
    },
    deleteUser: (
      state: UserState,
      action: { type: string; payload: number },
    ) => {
      let idx = state.userList.items.findIndex(
        (item) => item.userAccountID === action.payload,
      );
      if (idx >= 0) {
        state.userList.items.splice(idx, 1);
        state.userList.items = [...state.userList.items];
        state.userList.itemCount = state.userList.itemCount - 1;
      }

      idx = state.onboardingUsers.findIndex(
        (item) => item.userAccountID === action.payload,
      );
      if (idx >= 0) {
        state.onboardingUsers.splice(idx, 1);
        state.onboardingUsers = [...state.onboardingUsers];
      }
    },

    setUserRoles: (state: UserState, action: { payload: UserRole[] }) => {
      state.loadingUserRoles = false;
      state.loadedUserRoles = true;
      state.userRoles = action.payload;
    },
    setLoadingUserRoles: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingUserRoles = action.payload;
    },
    setLoadedUserRoles: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedUserRoles = action.payload;
    },

    setProviderSearchFilter: (
      state: UserState,
      action: { payload: string },
    ) => {
      state.providerSearchFilter = action.payload;
    },
    setProviderList: (
      state: UserState,
      action: { payload: PagedList<ProviderOptimized> },
    ) => {
      state.loadingProviderList = false;
      state.loadedProviderList = true;
      state.providerList = action.payload;
    },
    setLoadingProviderList: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingProviderList = action.payload;
    },
    setLoadedProviderList: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedProviderList = action.payload;
    },
    setOnboardingUser: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.onboardingUser = action.payload;
    },
    setOnboardedUser: (
      state: UserState,
      action: { type: string; payload: boolean },
    ) => {
      state.onboardedUser = action.payload;
    },
    setOnboardingUserError: (
      state: UserState,
      action: { type: string; payload: string | null },
    ) => {
      state.onboardingUserError = action.payload;
    },
    setOnboardingUserSuccess: (
      state: UserState,
      action: { type: string; payload: string | null },
    ) => {
      state.onboardingUserSuccess = action.payload;
    },
    resetOnboardingFlags: (state: UserState) => {
      state.onboardingUser = false;
      state.onboardedUser = false;
      state.onboardingUserError = null;
      state.onboardingUserSuccess = null;
    },
    setUserSnackbarMessage: (
      state: UserState,
      action: { type: string; payload: string | null },
    ) => {
      state.userSnackbarMessage = action.payload;
    },
    setUserSnackbarError: (
      state: UserState,
      action: { type: string; payload: string | null },
    ) => {
      state.userSnackbarError = action.payload;
    },
  },
});

export const { setUserInfo } = slice.actions;
export const { setLoadingUserInfo } = slice.actions;
export const { setLoadedUserInfo } = slice.actions;
export const { setEULAInfo } = slice.actions;
export const { setUserEULA } = slice.actions;
export const { setExternalSupportAgreementInfo } = slice.actions;
export const { setUserExternalSupportAgreement } = slice.actions;

export const { setSearchFilter } = slice.actions;
export const { setSelectedFilter } = slice.actions;
export const { setPage } = slice.actions;
export const { setPageSize } = slice.actions;
export const { setSortModel } = slice.actions;
export const { setLoadingUserList } = slice.actions;
export const { setLoadedUserList } = slice.actions;
export const { setUserList } = slice.actions;
export const { setOnboardingUsers } = slice.actions;

export const { setLoadingUser } = slice.actions;
export const { setLoadedUser } = slice.actions;
export const { setSavingUser } = slice.actions;
export const { setSavedUser } = slice.actions;
export const { setSavingError } = slice.actions;
export const { resetSavingFlags } = slice.actions;
export const { setUser } = slice.actions;
export const { addUser } = slice.actions;
export const { updateUser } = slice.actions;
export const { deleteUser } = slice.actions;
export const { setLoadingUserRoles } = slice.actions;
export const { setLoadedUserRoles } = slice.actions;
export const { setUserRoles } = slice.actions;
export const { setProviderSearchFilter } = slice.actions;
export const { setLoadingProviderList } = slice.actions;
export const { setLoadedProviderList } = slice.actions;
export const { setProviderList } = slice.actions;
export const { setOnboardingUser } = slice.actions;
export const { setOnboardedUser } = slice.actions;
export const { setOnboardingUserError } = slice.actions;
export const { setOnboardingUserSuccess } = slice.actions;
export const { resetOnboardingFlags } = slice.actions;
export const { setUserSnackbarMessage } = slice.actions;
export const { setUserSnackbarError } = slice.actions;

export const fetchUserInfoAsync = () => (dispatch: Function) => {
  dispatch(setLoadingUserInfo(true));
  dispatch(setLoadedUserInfo(false));
  userService.getUserInfo().subscribe({
    next: (userInfo: User) => {
      const pattern = /^tp\..*\.user$/;
      const containsMatchingString = userInfo?.access?.userRoles?.some(
        (str: string | number) => {
          if (typeof str === 'number') {
            return false;
          }
          return pattern.test(str);
        },
      );
      const isOnboardingUser = !!userInfo.onboardingUser;

      if (containsMatchingString || isOnboardingUser) {
        dispatch(setUserInfo(userInfo));
        dispatch(setLoadingUserInfo(false));
        dispatch(setLoadedUserInfo(true));
      } else {
        dispatch(
          setUserInfo({
            emailAddress: 'invalid@americanlogistics.com',
            access: {
              userRoles: [],
            },
          }),
        );
        dispatch(setLoadingUserInfo(false));
        dispatch(setLoadedUserInfo(true));
      }
    },
    error: (err) => {
      // This is here to catch the case where an internal user is assigned
      // to the application but they don't have any roles assigned. Adding
      // this fake user to the store causes the code in the PrivateRoute
      // guard to navigate to an error page that directs them to call IT
      if (err.messageId === 'INVALID_USER_ACCOUNT') {
        dispatch(
          setUserInfo({
            emailAddress: 'invalid@americanlogistics.com',
            access: {
              userRoles: [],
            },
          }),
        );
        dispatch(setLoadingUserInfo(false));
        dispatch(setLoadedUserInfo(true));
      }
    },
  });
};

export const getEULAInfoAsync =
  (applicationTitleID: number) => (dispatch: Function) => {
    userService.getEULAInfo(applicationTitleID).subscribe((info) => {
      dispatch(setEULAInfo(info));
    });
  };

export const updateEULAAgreementAsync =
  (userId: number, version: number) => (dispatch: Function) => {
    userService.setEulaFlag(userId, version).subscribe(() => {
      dispatch(setUserEULA(true));
    });
  };

export const getExternalSupportAgreementInfoAsync =
  (applicationTitleID: number) => (dispatch: Function) => {
    userService
      .getExternalSupportAgreementInfo(applicationTitleID)
      .subscribe((info) => {
        dispatch(setExternalSupportAgreementInfo(info));
      });
  };

export const updateExternalSupportAgreementAsync =
  (userId: number, version: number) => (dispatch: Function) => {
    userService
      .setExternalSupportAgreementFlag(userId, version)
      .subscribe(() => {
        dispatch(setUserExternalSupportAgreement(true));
      });
  };

export const fetchUserListAsync =
  (
    providerId: number,
    pageSize: number,
    page: number,
    filter: string,
    search: string,
    sortModel: SortModel | null,
    includeRoles: boolean,
  ) =>
  (dispatch: Function) => {
    dispatch(setLoadingUserList(true));
    dispatch(setLoadedUserList(false));
    userService
      .findUsers(
        providerId,
        pageSize,
        page,
        filter,
        search,
        sortModel,
        includeRoles,
      )
      .subscribe((userList) => {
        dispatch(setUserList(userList));
        dispatch(setLoadingUserList(false));
        dispatch(setLoadedUserList(true));
      });
  };

export const refetchUserListAsync =
  (providerId: number) => (dispatch: Function, getState: Function) => {
    const state = getState();
    const userState = state.user;
    dispatch(
      fetchUserListAsync(
        providerId,
        userState.pageSize,
        userState.page,
        userState.selectedFilter,
        userState.searchFilter,
        userState.sortModel,
        true,
      ),
    );
  };

export const fetchOnboardingUsersAsync =
  (providerId: number) => (dispatch: Function) => {
    userService.findOnboardingUsers(providerId).subscribe((userList) => {
      dispatch(setOnboardingUsers(userList.items));
    });
  };

export const fetchUserAsync = (userId: number) => (dispatch: Function) => {
  dispatch(setLoadingUser(true));
  dispatch(setLoadedUser(false));
  userService.getUser(userId).subscribe((user) => {
    dispatch(setUser(user));
    dispatch(setLoadingUser(false));
    dispatch(setLoadedUser(true));
  });
};

const handleSaveUserSuccess = (
  dispatch: Function,
  user: User,
  updateFn: Function,
) => {
  dispatch(updateFn(user));
  dispatch(setSavingUser(false));
  dispatch(setSavedUser(true));
};

const handleSaveUserError = (dispatch: Function, error: any) => {
  dispatch(setSavingUser(false));
  dispatch(setSavedUser(false));
  // This should be localized from messageId
  dispatch(setSavingError(error.message));
};

export const createUserAsync = (user: User) => (dispatch: Function) => {
  dispatch(setUser(null));
  dispatch(setSavingUser(true));
  dispatch(setSavedUser(false));
  dispatch(setSavingError(null));

  userService.createUser(user).subscribe({
    next(createdUser: User) {
      handleSaveUserSuccess(dispatch, createdUser, addUser);
      if (user.transportationProviderID) {
        dispatch(fetchOnboardingUsersAsync(user.transportationProviderID));
      }
    },
    error(err: any) {
      handleSaveUserError(dispatch, err);
      return null;
    },
  });
};

export const updateUserAsync = (user: User) => (dispatch: Function) => {
  dispatch(setSavingUser(true));
  dispatch(setSavedUser(false));
  dispatch(setSavingError(null));
  userService.updateUser(user).subscribe({
    next(updatedUser: User) {
      handleSaveUserSuccess(dispatch, updatedUser, updateUser);
    },
    error(err: any) {
      handleSaveUserError(dispatch, err);
      return null;
    },
  });
};

export const fetchUserRolesAsync =
  (categoryId: number, appTitleId?: number) => (dispatch: Function) => {
    dispatch(setUserRoles([]));
    dispatch(setLoadingUserRoles(true));
    dispatch(setLoadedUserRoles(false));
    userService.getUserRoles(categoryId, appTitleId).subscribe((userRoles) => {
      dispatch(setUserRoles(userRoles));
      dispatch(setLoadingUserRoles(false));
      dispatch(setLoadedUserRoles(true));
    });
  };

export const fetchProviderListAsync =
  (
    pageSize: number,
    page: number,
    search: string,
    sortModel: SortModel | null,
  ) =>
  (dispatch: Function) => {
    dispatch(setLoadingProviderList(true));
    dispatch(setLoadedProviderList(false));
    providerService
      .findProviders(pageSize, page, '', search, sortModel)
      .subscribe((providerList) => {
        dispatch(setProviderList(providerList));
        dispatch(setLoadingProviderList(false));
        dispatch(setLoadedProviderList(true));
      });
  };

export const onboardUserAsync =
  (
    firstName: string,
    lastName: string,
    email: string,
    providerId?: number,
    providerName?: string,
  ) =>
  (dispatch: Function) => {
    dispatch(setOnboardingUser(true));
    dispatch(setOnboardedUser(false));
    userService
      .onboardUser(firstName, lastName, email, providerId, providerName)
      .subscribe({
        next() {
          dispatch(setOnboardingUser(false));
          dispatch(setOnboardedUser(true));
          dispatch(
            setOnboardingUserSuccess(
              'The user has been successfully onboarded. They will receive an email to set their password.',
            ),
          );
          if (providerId) {
            dispatch(fetchOnboardingUsersAsync(providerId));
          }
        },
        error(err: any) {
          dispatch(setOnboardingUser(false));
          dispatch(setOnboardedUser(false));
          dispatch(
            setOnboardingUserError(
              err.message || 'Failed to onboard the new user',
            ),
          );
          return null;
        },
      });
  };

export const sendActivationEmailAsync =
  (userId: number, email: string) => (dispatch: Function) => {
    userService.sendActivationEmail(userId).subscribe({
      next() {
        const msg = `User activation email sent to ${email}`;
        dispatch(setUserSnackbarMessage(msg));
      },
      error(err: any) {
        dispatch(setUserSnackbarError('Failed to send user activation email'));
      },
    });
  };

export const resetUserPasswordAsync =
  (userId: number, email: string | any) => (dispatch: Function) => {
    userService.resetPassword(userId).subscribe({
      next() {
        const msg = `Password reset email sent to ${email}`;
        dispatch(setUserSnackbarMessage(msg));
      },
      error(err: any) {
        dispatch(setUserSnackbarError('Failed to send password reset email'));
      },
    });
  };

export const forgotPasswordAsync = (email: string) => (dispatch: Function) => {
  userService.forgotPassword(email).subscribe({
    next() {},
    error(err: any) {
      dispatch(setUserSnackbarError('Failed to send password reset email'));
    },
  });
};

export const deleteUserAsync =
  (providerId: number, userId: number, onboardedUsers: boolean) =>
  (dispatch: Function) => {
    userService.deleteUser(userId).subscribe(() => {
      if (onboardedUsers) {
        dispatch(refetchUserListAsync(providerId));
      } else {
        dispatch(deleteUser(userId));
      }
    });
  };

export const selectUserInfo = (state: { user: UserState }) =>
  state.user.userInfo;
export const selectLoadingUserInfo = (state: { user: UserState }) =>
  state.user.loadingUserInfo;
export const selectLoadedUserInfo = (state: { user: UserState }) =>
  state.user.loadedUserInfo;
export const selectEULAInfo = (state: { user: UserState }) =>
  state.user.eulaInfo;
export const selectExternalSupportAgreementInfo = (state: {
  user: UserState;
}) => state.user.externalSupportAgreementInfo;

export const selectSearchFilter = (state: { user: UserState }) =>
  state.user.searchFilter;
export const selectSelectedFilter = (state: { user: UserState }) =>
  state.user.selectedFilter;
export const selectPage = (state: { user: UserState }) => state.user.page;
export const selectPageSize = (state: { user: UserState }) =>
  state.user.pageSize;
export const selectSortModel = (state: { user: UserState }) =>
  state.user.sortModel;
export const selectLoadingUserList = (state: { user: UserState }) =>
  state.user.loadingUserList;
export const selectLoadedUserList = (state: { user: UserState }) =>
  state.user.loadedUserList;
export const selectUserList = (state: { user: UserState }) =>
  state.user.userList;
export const selectOnboardingUsers = (state: { user: UserState }) =>
  state.user.onboardingUsers;

export const selectLoadingUser = (state: { user: UserState }) =>
  state.user.loadingUser;
export const selectLoadedUser = (state: { user: UserState }) =>
  state.user.loadedUser;
export const selectSavingUser = (state: { user: UserState }) =>
  state.user.savingUser;
export const selectSavedUser = (state: { user: UserState }) =>
  state.user.savedUser;
export const selectSavingError = (state: { user: UserState }) =>
  state.user.savingError;
export const selectUser = (state: { user: UserState }) => state.user.user;

export const selectLoadingUserRoles = (state: { user: UserState }) =>
  state.user.loadingUserRoles;
export const selectLoadedUserRoles = (state: { user: UserState }) =>
  state.user.loadedUserRoles;
export const selectUserRoles = (state: { user: UserState }) =>
  state.user.userRoles;

export const selectProviderSearchFilter = (state: { user: UserState }) =>
  state.user.providerSearchFilter;
export const selectLoadingProviderList = (state: { user: UserState }) =>
  state.user.loadingProviderList;
export const selectLoadedProviderList = (state: { user: UserState }) =>
  state.user.loadedProviderList;
export const selectProviderList = (state: { user: UserState }) =>
  state.user.providerList;
export const selectOnboardingUser = (state: { user: UserState }) =>
  state.user.onboardingUser;
export const selectOnboardedUser = (state: { user: UserState }) =>
  state.user.onboardedUser;
export const selectOnboardingUserError = (state: { user: UserState }) =>
  state.user.onboardingUserError;
export const selectOnboardingUserSuccess = (state: { user: UserState }) =>
  state.user.onboardingUserSuccess;
export const selectUserSnackbarMessage = (state: { user: UserState }) =>
  state.user.userSnackbarMessage;
export const selectUserSnackbarError = (state: { user: UserState }) =>
  state.user.userSnackbarError;

export default slice.reducer;
